import { SchemaForm } from "components/Form/components/SchemaForm/SchemaForm";
import { Execution } from "graphql/generated/graphql";
import { useContext, useMemo } from "react";
import { RouterContext } from "utils/RouterManager/RouterManager";

interface ExecutionSchemaTabProps {
    activeTab: string;
    schemaField: "input_schema" | "output_schema";
    resourceData: Execution;
}

/**
 * Read-only form-builder view of an execution's `input_schema` / `output_schema`.
 *
 * Executions store the immutable input/output schemas that were in effect when
 * the execution was launched. The FLOW record screen already renders these
 * schemas with the schema form-builder (the meta-schema editor); this component
 * gives the EXECUTION screen the same rendering — always read-only, since an
 * execution's schemas are historical and never edited.
 *
 * It mirrors the flow form-builder wiring: `values` is the actual schema being
 * displayed and `schema` is the meta-schema (`filteredSchemaFormSchema`) that
 * describes the form structure. `SchemaForm` supplies the Form-view / Code-view
 * toggle itself, so the previous raw code-editor view is preserved as "Code view".
 */
export const ExecutionSchemaTab = ({
    activeTab,
    schemaField,
    resourceData,
}: ExecutionSchemaTabProps) => {
    const schemaFormSchema = useContext(RouterContext).filteredSchemaFormSchema;

    const value = useMemo(
        () => ({
            values: (resourceData as any)?.[schemaField] ?? null,
            schema: schemaFormSchema,
        }),
        [resourceData, schemaField, schemaFormSchema]
    );

    return (
        <div key={`${activeTab}-readonlySchemaForm`} className="schemaFormTab">
            <SchemaForm inputProps={{ value, isReadOnly: true }} />
        </div>
    );
};
